// src/components/MarketHeatGauge.tsx
import React, { useEffect, useRef, useState } from 'react';

interface MarketHeatGaugeProps {
  score: number;
  interpretation: 'Cold' | 'Cool' | 'Neutral' | 'Warm' | 'Hot' | 'Overheated';
  components?: {
    fearGreedIndex: number;
    marketCapProximity: number;
    googleTrends: number;
    rsi: number;
    unstakingQueue: number;
    newsActivity: number;
    walletActivity: number;
  };
}

const MarketHeatGauge: React.FC<MarketHeatGaugeProps> = ({
  score,
  interpretation,
  components
}) => {
  const [animatedScore, setAnimatedScore] = useState<number>(0);
  const requestRef = useRef<number>();
  const startTimeRef = useRef<number>();
  const previousScoreRef = useRef<number>(0);

  // Get color based on interpretation
  const getColor = (interpretation: string): string => {
    switch (interpretation) {
      case 'Cold':
        return '#3b82f6'; // Blue
      case 'Cool':
        return '#60a5fa'; // Light Blue
      case 'Neutral':
        return '#fcd34d'; // Yellow
      case 'Warm':
        return '#f59e0b'; // Orange
      case 'Hot':
        return '#ef4444'; // Red
      case 'Overheated':
        return '#b91c1c'; // Dark Red
      default:
        return '#8a92b2'; // Neutral gray
    }
  };

  // Get component label
  const getComponentLabel = (key: string): string => {
    switch (key) {
      case 'fearGreedIndex':
        return 'Fear & Greed';
      case 'marketCapProximity':
        return 'Market Cap';
      case 'googleTrends':
        return 'Google Trends';
      case 'rsi':
        return 'ETH RSI';
      case 'unstakingQueue':
        return 'Unstaking Queue';
      case 'openInterest':
        return 'Open Interest';
      case 'newsActivity':
        return 'News Activity';
      case 'walletActivity':
        return 'Wallet Activity';
      default:
        return key;
    }
  };

  // Start animation when score changes
  useEffect(() => {
    previousScoreRef.current = animatedScore;
    startTimeRef.current = undefined; // Reset start time for new animation

    const animate = (timestamp: number) => {
      if (startTimeRef.current === undefined) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const duration = 1500; // Animation duration in ms
      
      // Calculate progress (0 to 1)
      const progress = Math.min(elapsed / duration, 1);
      
      // Animate from previous score to new score
      const currentScore = previousScoreRef.current + (score - previousScoreRef.current) * progress;
      
      setAnimatedScore(currentScore);
      
      if (progress < 1) {
        requestRef.current = requestAnimationFrame(animate);
      }
    };

    requestRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [score, animatedScore]); // Added animatedScore as dependency

  // SVG gauge parameters
  const size = 240;
  const strokeWidth = 16;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * 0.75; // 270 degrees arc (not full circle)
  const startAngle = 225; // Start at bottom left (225 degrees)
  const color = getColor(interpretation);
  
  // Calculate the dash offset based on the score
  const dashOffset = arcLength - (arcLength * animatedScore) / 100;

  // SVG path for the arc
  const getArcPath = (radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(radius, startAngle);
    const end = polarToCartesian(radius, endAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
    
    return [
      `M ${start.x} ${start.y}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`
    ].join(' ');
  };

  // Convert polar coordinates to cartesian
  const polarToCartesian = (radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180;
    const x = size / 2 + radius * Math.cos(angleInRadians);
    const y = size / 2 + radius * Math.sin(angleInRadians);
    return { x, y };
  };

  // Background arc path (270 degrees)
  const backgroundArc = getArcPath(radius, startAngle, startAngle + 270);

  return (
    <div className="flex flex-col items-center">
      {/* Main gauge */}
      <div className="market-heat-gauge mb-8">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Background arc */}
          <path
            d={backgroundArc}
            fill="none"
            stroke="rgba(138, 146, 178, 0.2)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          
          {/* Value arc */}
          <path
            d={backgroundArc}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={arcLength}
            strokeDashoffset={dashOffset}
            transform={`rotate(${startAngle}, ${size / 2}, ${size / 2})`}
            style={{
              transformOrigin: 'center',
              transition: 'stroke-dashoffset 1.5s ease-in-out, stroke 0.5s ease'
            }}
          />
          
          {/* Ticks for 0, 25, 50, 75, 100 */}
          {[0, 25, 50, 75, 100].map((tick) => {
            const tickAngle = startAngle + (tick / 100) * 270;
            const tickStart = polarToCartesian(radius - strokeWidth / 2, tickAngle);
            const tickEnd = polarToCartesian(radius + strokeWidth / 2, tickAngle);
            
            return (
              <line
                key={tick}
                x1={tickStart.x}
                y1={tickStart.y}
                x2={tickEnd.x}
                y2={tickEnd.y}
                stroke="rgba(138, 146, 178, 0.5)"
                strokeWidth={2}
              />
            );
          })}
          
          {/* Score label */}
          <text
            x={size / 2}
            y={size / 2 - 10}
            textAnchor="middle"
            fontSize="36"
            fontWeight="bold"
            fill="currentColor"
          >
            {Math.round(animatedScore)}
          </text>
          
          {/* Interpretation label */}
          <text
            x={size / 2}
            y={size / 2 + 20}
            textAnchor="middle"
            fontSize="16"
            fill="currentColor"
          >
            {interpretation}
          </text>
        </svg>
      </div>
      
      {/* Component breakdown */}
      {components && (
        <div className="w-full max-w-3xl">
          <h3 className="text-lg font-semibold mb-3">Component Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(components).map(([key, value]) => (
              <div key={key} className="bg-dashboard-card-light dark:bg-dashboard-card-dark rounded-lg p-3 shadow-sm">
                <div className="text-sm text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
                  {getComponentLabel(key)}
                </div>
                <div className="flex items-center">
                  <div className="text-lg font-medium">{Math.round(value)}</div>
                  <div className="ml-2 flex-1">
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full" 
                        style={{
                          width: `${value}%`,
                          backgroundColor: getColor(
                            value <= 20 ? 'Cold' :
                            value <= 40 ? 'Cool' :
                            value <= 60 ? 'Neutral' :
                            value <= 75 ? 'Warm' :
                            value <= 90 ? 'Hot' : 'Overheated'
                          )
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketHeatGauge;
